import { z } from 'zod';

/**
 * Reusable primitive schemas shared by per-step validation files.
 * Validation messages are simple keys consumed via t(`validation.${key}`).
 */

export const emailSchema = z.string().min(1, 'required').email('invalidEmail');

/**
 * Israeli mobile phone: exactly 10 digits, must start with 05.
 * Example: 0501234567
 */
export const mobilePhoneSchema = z
  .string()
  .min(1, 'required')
  .regex(/^05\d{8}$/, 'invalidMobile');

/**
 * Standard Teudat Zehut / Israeli company tax ID checksum:
 * 9 digits, alternating *1 / *2 multipliers, digit-sum of products, mod 10 == 0.
 */
export const isValidIsraeliId = (id: string): boolean => {
  if (!/^\d{9}$/.test(id)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    let n = Number(id[i]) * ((i % 2) + 1);
    if (n > 9) n -= 9;
    sum += n;
  }
  return sum % 10 === 0;
};

/**
 * Israeli company number (ח.פ.) from the Corporations Authority.
 * Same checksum as a Teudat Zehut, allocated in the 51xxxxxx range.
 * An authorized dealer (עוסק מורשה) uses the owner's personal ID, which does not start with 51.
 */
export const isCompanyTaxId = (id: string): boolean =>
  id.startsWith('51') && isValidIsraeliId(id);

export const israeliIdSchema = z
  .string()
  .min(1, 'required')
  .refine(isValidIsraeliId, { message: 'invalidIsraeliId' });

export const israeliTaxIdSchema = z
  .string()
  .min(1, 'required')
  .refine(isValidIsraeliId, { message: 'invalidTaxId' });

export const numericSchema = (min = 1) =>
  z.string().min(min, 'required').regex(/^\d+$/, 'numericOnly');
